// NPM Imports
import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { withRouter, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Toastr, Tooltip } from "../../revvComponents";

// Components Imports
import { ConfirmationBox } from "../../commonComponents";
import CreateDocPopup from "../CreateDocPopup";
import OppoSharingPopup from "./OppoSharingPopup";
import AssetDetailsPopupWrapper from "../AssetDetailsPopupWrapper";
import PipelineSwimlane from "./PipelineSwimLane";
import PipelineHeader from "./PipelineHeader";
import PipelineLeftFilter from "./PipelineLeftFilter";

// Actions Imports
import PipelineActions from "../../actions/PipelineActions";
import { getAllDocumentTypes } from "../../actions/DocumentActions";

// Service Imports
import { deleteDocument } from "../../actions/DocumentActions/services";
import { fetchOpportunityByID } from "../../actions/PipelineActions/pipelineServices";

// Internal Imports
import { PIPELINE } from "../../constants";
import { OppoUtils, GlobalUtils } from "../../utils";

// Destructuring
const { deleteDocumentAction, getOpportunitiesForPipeline } = PipelineActions;
const { quickFilterOpportunities } = OppoUtils;
const {
  BY_LAST_UPDATE,
  DEFAULT_SELECTED_LUKE_ANALYTICS,
  DEFAULT_SELECTED_SAMPLE_OPPORTUNITY,
} = PIPELINE;
const { handleApiErrMsg, isFreeTrailBillingPlan, readDataFromLocalStorage } =
  GlobalUtils;

class PipelineWrapper extends Component {
  assetDetailDropdownRef = React.createRef();

  state = {
    searchTerm: "",
    showCreateDocumentPopup: false,
    showConfirmDeletePopup: false,
    showAssetDetailsPopup: false,
    documentToBeDeleted: {},
    currentSelectedOpp: "",
    // create a document from an external source
    opportunityByOppID: null,
    isCreateDocFromExternalSource: false,
    showShareOppoPopup: false,
    externalQuote: null,
  };

  componentDidMount() {
    const { orgID, title, titleID: oppID, titleFor } = this.props.match.params;

    // check if the url contains the document creation part
    if (orgID || title || oppID || titleFor) {
      if (title === "opportunity" && oppID && titleFor === "newdoc") {
        this._createDocumentFromExternalSourceHandler(parseInt(oppID, 10));
      } else {
        Toastr.showToastr({
          type: "failure",
          message: "Invalid URL ! Can't create a document",
          timeout: 3000,
        });
        setTimeout(() => {
          this.props.history.push("/pipeline");
        }, 1000);
      }
    }

    // Select the "Luke Analytics" or "Sample Opportunity" opportunity as a
    // default opportunity, if user is in a Free Trail
    this._selectDefaultOpportunity();
  }

  // handle the selection of default opportunity
  _selectDefaultOpportunity = () => {
    if (isFreeTrailBillingPlan()) {
      const user = readDataFromLocalStorage("revvsalesUser");
      let currentDefaultSelectedOpp;
      // Check if an opportunity is already selected
      if ("opportunityFilter" in user && "name" in user.opportunityFilter) {
        currentDefaultSelectedOpp = this.props.opportunities.find(
          opp => opp.id === user.opportunityFilter.id,
        );
      }
      // If an opportunity is not selected then, select the "Default Opportunity"
      if (!currentDefaultSelectedOpp) {
        const defaultSelectedOpp = this.props.opportunities.filter(
          opp =>
            opp.name === DEFAULT_SELECTED_LUKE_ANALYTICS ||
            opp.name === DEFAULT_SELECTED_SAMPLE_OPPORTUNITY,
        );
        if (defaultSelectedOpp && defaultSelectedOpp.length > 0) {
          currentDefaultSelectedOpp = { ...defaultSelectedOpp[0] };
        }
      }
      if (currentDefaultSelectedOpp) {
        this._updatePipelineState(
          "currentSelectedOpp",
          currentDefaultSelectedOpp,
        );
        this.props.getOpportunitiesForPipeline(
          new Array(currentDefaultSelectedOpp),
        );
      }
    }
  };

  // handle when create a  document from external source
  // ex: /pipeline/2/opportunity/9/newdoc
  _createDocumentFromExternalSourceHandler = oppID => {
    fetchOpportunityByID(oppID)
      .then(response => {
        this.setState({
          opportunityByOppID: response.Opportunity,
          isCreateDocFromExternalSource: true,
        });
      })
      .catch(error => {
        Toastr.showToastr({
          type: "failure",
          message: handleApiErrMsg(error),
          timeout: 3000,
        });
        setTimeout(() => {
          this.props.history.push("/pipeline");
        }, 1000);
      });
  };

  _updatePipelineState = (key, value) => {
    this.setState({
      [key]: value,
    });
  };

  _updateMultiplePipelineState = object => {
    this.setState(object);
  };

  _handleDeleteDocument = (docID, extQuote) => {
    let currentPipelineData = [];
    if (this.state.currentSelectedOpp) {
      currentPipelineData = new Array(this.state.currentSelectedOpp);
    }
    this.props.deleteDocumentAction(
      docID,
      currentPipelineData,
      this.props.opportunities,
      extQuote,
    );
  };

  // Update State :: "currentSelectedOpp" when delete document
  _updateCurrentSelectedOpps = (docID, currentSelectedOpp, deletedExtQuote) => {
    const currentOpp = { ...currentSelectedOpp };
    const updateExtQuotes = [...currentOpp.external_quotes];
    const filteredDocs = [];
    // remove the deleted doc from the opportunity's docs and
    // add the removed external quote in opportunity's external_quotes
    currentOpp.docs.forEach(doc => {
      if (doc.id === docID) {
        if (deletedExtQuote && deletedExtQuote.length > 0) {
          updateExtQuotes.push(deletedExtQuote[0]);
        }
      } else {
        filteredDocs.push(doc);
      }
    });
    currentOpp.docs = filteredDocs;
    currentOpp.external_quotes = updateExtQuotes;
    this.setState({
      currentSelectedOpp: currentOpp,
    });
  };

  // --------- Document related methods start here -------------
  _openCreateDocumentPopup = (opportunity, isExtQuote) => {
    let stateField = "currentSelectedOpp";
    if (isExtQuote) {
      stateField = "externalQuote";
    }
    this.setState(() => ({
      [stateField]: opportunity,
      showCreateDocumentPopup: true,
    }));
  };

  _closeCreateDocumentPopup = () => {
    this.setState({
      showCreateDocumentPopup: false,
      isCreateDocFromExternalSource: false,
    });
  };

  _confirmDeleteDocument = documentData => {
    this.setState(() => ({
      documentToBeDeleted: documentData,
      showConfirmDeletePopup: true,
    }));
  };

  _deleteDocument = docID => {
    deleteDocument(docID).then(response => {
      let extQuote;
      if (response.quotes) {
        extQuote = response.quotes;
      }
      // update the current selected opportunity
      if (this.state.currentSelectedOpp) {
        this._updateCurrentSelectedOpps(
          docID,
          this.state.currentSelectedOpp,
          extQuote,
        );
      }
      this.setState(
        () => ({
          documentToBeDeleted: {},
        }),
        () => this._handleDeleteDocument(docID, extQuote),
      );
      this._closeConfirmDeletePopup();
    });
  };

  _closeConfirmDeletePopup = () => {
    this.setState({ showConfirmDeletePopup: false });
  };

  // -------- Document related methods end here ----------------

  // toggle "showAssetDetailsPopup" when mouse hover on opportuity name
  _toggleAssetDetailsPopup = () => {
    // attach an event listener when onhover
    if (!this.state.showAssetDetailsPopup) {
      document.addEventListener(
        "mouseover",
        this._handleAssetDetailDropdownOutsideClick,
        false,
      );
      this.setState({
        showAssetDetailsPopup: true,
      });
    }
  };

  _handleAssetDetailDropdownOutsideClick = e => {
    const node = this.assetDetailDropdownRef.current;
    // ignore clicks on the component itself
    if (node && node.contains(e.target)) {
      return;
    }
    this.setState({
      showAssetDetailsPopup: false,
    });

    // remove an event listener when onhover
    document.removeEventListener(
      "mouseover",
      this._handleAssetDetailDropdownOutsideClick,
      false,
    );
  };

  _getTotalDocuments = opportunities => {
    let count = 0;
    opportunities.forEach(opportunity => {
      count += opportunity.docs.length;
    });
    return count;
  };

  _toggleShareOppoPopup = state => {
    this.setState({ showShareOppoPopup: state });
  };

  render() {
    const { documentTypes, opportunities, pipelineData } = this.props;
    const {
      searchTerm,
      externalQuote,
      opportunityByOppID,
      currentSelectedOpp,
      documentToBeDeleted,
      showAssetDetailsPopup,
      showConfirmDeletePopup,
      showCreateDocumentPopup,
      isCreateDocFromExternalSource,
      showShareOppoPopup,
    } = this.state;
    let popupSpace = null;
    let documentDeleteConfirmationPopupSpace = null;

    // Create Document Popup
    if (showCreateDocumentPopup || isCreateDocFromExternalSource) {
      popupSpace = (
        <CreateDocPopup
          quote={externalQuote}
          opportunity={
            isCreateDocFromExternalSource
              ? opportunityByOppID
              : currentSelectedOpp
          }
          show={showCreateDocumentPopup || isCreateDocFromExternalSource}
          closePopup={this._closeCreateDocumentPopup}
          documentTypes={documentTypes}
          source="pipeline"
        />
      );
    }
    // Delete Document Confirmation Box
    if (showConfirmDeletePopup) {
      documentDeleteConfirmationPopupSpace = (
        <ConfirmationBox
          show={showConfirmDeletePopup}
          closePopup={this._closeConfirmDeletePopup}
          header={`Are you sure you want to delete Document: '${documentToBeDeleted.documentNo}' ?`}
          subHeader={`Once deleted, you won't be able to access this document.`}
          checkboxLabel="Yes, I want to delete."
          data={documentToBeDeleted.documentId}
          confirmAction={this._deleteDocument}
          okBtnText="Delete document"
        />
      );
    }

    let totalDocs = 0;
    if (opportunities && opportunities.length > 0) {
      totalDocs = this._getTotalDocuments(
        quickFilterOpportunities(opportunities, searchTerm),
      );
    }

    let accountName = `${totalDocs} Documents`;
    if (currentSelectedOpp) {
      if (
        currentSelectedOpp.crm_account &&
        currentSelectedOpp.crm_account.name
      ) {
        accountName = (
          <Link to={`/assets/${currentSelectedOpp.crm_account.id}`}>
            {currentSelectedOpp.crm_account.name}
          </Link>
        );
      } else {
        accountName = "-";
      }
    }

    return (
      <Fragment>
        {popupSpace}
        {documentDeleteConfirmationPopupSpace}
        {showShareOppoPopup && (
          <OppoSharingPopup
            oppo={this.state.currentSelectedOpp}
            closePopup={() => this._toggleShareOppoPopup(false)}
          />
        )}

        {/* -------- Pipeline Header Section -------- */}
        <PipelineHeader />

        <div className="pipelinewrapper">
          {/* -------- Pipeline Left Filter Section -------- */}
          {opportunities && (
            <PipelineLeftFilter
              openCreateDocumentPopup={this._openCreateDocumentPopup}
              searchTerm={searchTerm}
              currentSelectedOpp={currentSelectedOpp}
              updatePipelineState={this._updatePipelineState}
              updateMultiplePipelineState={this._updateMultiplePipelineState}
            />
          )}

          {/* -------- Pipeline Right Section -------- */}
          <div className="pipelineoppurtunity-wrapper pipelineoppurtunity-full-width">
            {/* -------- Pipeline Right Header Section -------- */}
            <div className="pipelineoppurtunity-heading-wrapper">
              <div
                className="pipelineoppurtunity-heading"
                data-test="pipelinewrapper-pipelineopportunity-heading-section">
                <span
                  className="pipelineoppurtunity-heading-icon"
                  data-test="pipelinewrapper-pipelineopportunity-heading-icon">
                  <FontAwesomeIcon icon={["far", "crown"]} />
                </span>
                <div className="pipelineoppurtunity-heading-info">
                  <div
                    className="pipelineoppurtunity-heading-title"
                    data-test="pipelinewrapper-pipelineopportunity-heading-title">
                    {currentSelectedOpp
                      ? currentSelectedOpp.name
                      : "All Opportunities"}
                  </div>
                  <div
                    className="pipelineoppurtunity-heading-meta"
                    data-test="pipelinewrapper-pipelineopportunity-heading-meta">
                    {accountName}
                  </div>
                </div>
                <div className="oppo-share-wrapper">
                  {currentSelectedOpp.scope &&
                    currentSelectedOpp.scope.Share && (
                      <Tooltip
                        placement="top"
                        align={{
                          offset: [0, 4],
                        }}
                        overlay="Share Opportunity">
                        <div
                          data-test="opportunity-sharing-icon"
                          className="oppo-share-btnlarge"
                          onClick={() => this._toggleShareOppoPopup(true)}
                          onKeyPress={() => this._toggleShareOppoPopup(true)}
                          role="button"
                          tabIndex="0">
                          <FontAwesomeIcon icon={["fas", "user-plus"]} />
                        </div>
                      </Tooltip>
                    )}
                </div>
              </div>
              {currentSelectedOpp && (
                <div className="pipelineoppurtunity-meta">
                  <ul>
                    <li>
                      <h5>Status</h5>
                      <h3>
                        <div className="pipelineoppurtunity-status">
                          <span>{currentSelectedOpp.stage_name}</span>
                        </div>
                      </h3>
                    </li>
                    <li>
                      <h5>Close Date</h5>
                      <h3>
                        {GlobalUtils.formatDate(
                          currentSelectedOpp.close_date,
                          "ll",
                        )}
                      </h3>
                    </li>
                    <li>
                      <h5>Expected Revenue</h5>
                      <h3>${currentSelectedOpp.expected_revenue}</h3>
                    </li>
                    <li className="pipelinewrapper-assetdetails-icon">
                      <span
                        className={
                          showAssetDetailsPopup
                            ? "pipelinewrapper-assetdetails-activeicon"
                            : ""
                        }
                        onMouseOver={this._toggleAssetDetailsPopup}
                        onFocus={this._toggleAssetDetailsPopup}
                        role="button"
                        tabIndex="0">
                        <FontAwesomeIcon icon={["far", "ellipsis-h"]} />
                      </span>
                    </li>
                    <AssetDetailsPopupWrapper
                      isFetch
                      opportunity={currentSelectedOpp}
                      show={showAssetDetailsPopup}
                      assetDetailDropdownRef={this.assetDetailDropdownRef}
                      positionClassName="pipelinewrapper-assetdetailpopup-position"
                    />
                  </ul>
                </div>
              )}
            </div>

            {/* -------- Pipeline Swimlane Section -------- */}
            <div className="pipelinecolumns-wrap">
              <div className="pipelinecolumns">
                {pipelineData && pipelineData.length > 0
                  ? pipelineData.map(swimlane => (
                      <PipelineSwimlane
                        key={swimlane.context}
                        swimlane={swimlane}
                        opportunities={OppoUtils.sortOppsDocs(
                          quickFilterOpportunities(
                            swimlane.opportunitiesByDocs,
                            searchTerm,
                          ),
                          BY_LAST_UPDATE,
                          true,
                        )}
                        selectedOpp={currentSelectedOpp}
                        openCreateDocumentPopup={this._openCreateDocumentPopup}
                        openCreateOrderFormPopup={
                          this._openCreateOrderFormPopup
                        }
                        closeCreateDocumentPopup={
                          this._closeCreateDocumentPopup
                        }
                        confirmDeleteDocument={this._confirmDeleteDocument}
                        dropdownRef={this.dropdownRef}
                      />
                    ))
                  : null}
              </div>
            </div>
          </div>
        </div>
      </Fragment>
    );
  }
}

const mapStateToProps = state => {
  const {
    opportunities,
    documentTypes,
    pipelineOpportunities: pipelineData,
  } = state.PipelineStore;
  return {
    pipelineData,
    opportunities,
    documentTypes,
  };
};

const mapDispatchToProps = {
  getDocumentTypes: () => getAllDocumentTypes(),
  deleteDocumentAction: (docID, opps, allOpportunities, extQuote) =>
    deleteDocumentAction(docID, opps, allOpportunities, extQuote),
  getOpportunitiesForPipeline: payload => getOpportunitiesForPipeline(payload),
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(PipelineWrapper));
